'use client';

import { logger } from '@adornis/base/logging.js';
import { AlertTriangle, ChevronDown, ChevronRight, ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCopyDetectionResultQuery } from '../../../../../../db/api/copy-detection.js';
import type { CopyDetectionResultRecord } from '../../../../../../db/copy-detection-result.js';
import { cn } from '../../../../../lib/utils.js';
import type { SummaryStateStore } from '../hooks/use-summary-state.js';

function formatScore(value: number): string {
  return value.toFixed(2);
}

export function CopyDetectionTab({ store }: { store: SummaryStateStore }) {
  const { t } = useTranslation();
  const { computed, props } = store;
  const examId = props.exam?._id;

  const [result, setResult] = useState<CopyDetectionResultRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedPairKey, setExpandedPairKey] = useState<string | null>(null);

  useEffect(() => {
    if (!examId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getCopyDetectionResultQuery(examId)({
      examId: 1,
      computedAt: 1,
      submissionCount: 1,
      meanScore: 1,
      stdDevScore: 1,
      excludedTaskNumbers: 1,
      checkedTaskNumbers: 1,
      flaggedPairIds: 1,
      pairs: {
        studentIdA: 1,
        studentIdB: 1,
        overallScore: 1,
        taskCount: 1,
        zScore: 1,
        flagged: 1,
        flagReason: 1,
        submissionSimilarity: 1,
        perTask: {
          taskNumber: 1,
          taskType: 1,
          studentAAnswer: 1,
          studentBAnswer: 1,
          rawSimilarity: 1,
          surprisalWeight: 1,
          wrongAnswerBoost: 1,
          weightedScore: 1,
          isWrongForBoth: 1,
          isVerbatim: 1,
        },
      },
    })
      .then(record => {
        if (!cancelled) setResult(record ?? null);
      })
      .catch(err => {
        if (cancelled) return;
        logger.error(err, 'Failed to load copy-detection result');
        setError(err instanceof Error ? err.message : String(err ?? ''));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [examId]);

  // Same fallback-name resolution the rest of the Summary canvas already uses (`Schüler N` for a blank OCR'd
  // name) — pairs are identified by submission `_id`, which is exactly what `StudentSummary.id` is.
  const nameById = new Map(computed.studentSummaries.map(s => [s.id, s.name]));
  const displayName = (submissionId: string): string => nameById.get(submissionId) ?? submissionId;

  if (isLoading) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">{t('Canvas.summary.copyCheck.loading', { defaultValue: 'Wird geladen…' })}</p>;
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 text-sm text-slate-500 dark:text-slate-400">
        {t('Canvas.summary.copyCheck.notReady', {
          defaultValue: 'Die Kopie-Prüfung läuft, sobald die Bewertung dieser Klausur abgeschlossen ist.',
        })}
      </div>
    );
  }

  const flaggedPairs = result.pairs.filter(pair => pair.flagged);

  const flagReasonLabel = (pair: (typeof flaggedPairs)[number]): string => {
    switch (pair.flagReason) {
      case 'identicalSubmission':
        return t('Canvas.summary.copyCheck.reason.identicalSubmission', { defaultValue: 'Nahezu identische Abgabe' });
      case 'verbatimText':
        return t('Canvas.summary.copyCheck.reason.verbatimText', { defaultValue: 'Wörtlich übereinstimmender Text' });
      case 'identicalAnswers':
        return t('Canvas.summary.copyCheck.reason.identicalAnswers', {
          defaultValue: 'Identische Antworten inkl. gleicher Fehler',
        });
      default:
        return t('Canvas.summary.copyCheck.zScore', { defaultValue: 'z = {{value}}', value: formatScore(pair.zScore) });
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 shadow-sm">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
          <ShieldAlert className="h-4 w-4" />
          {t('Canvas.summary.copyCheck.title', { defaultValue: 'Kopie-Prüfung' })}
        </h3>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {t('Canvas.summary.copyCheck.description', {
            defaultValue:
              'Vergleicht alle Abgaben paarweise Teilaufgabe für Teilaufgabe (Rechenwege werden dafür transkribiert) und markiert nahezu identische Abgaben, wörtlich übereinstimmende Texte, gemeinsame Fehler und statistisch auffällige Paare.',
          })}
        </p>
        {result.excludedTaskNumbers.length > 0 && (
          <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
            {t('Canvas.summary.copyCheck.excludedTasks', {
              defaultValue: 'Nicht geprüft (keine vergleichbare Textantwort): Aufgabe {{tasks}}',
              tasks: result.excludedTaskNumbers.join(', '),
            })}
          </p>
        )}
      </div>

      {flaggedPairs.length === 0 ? (
        result.checkedTaskNumbers?.length === 0 ? (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {t('Canvas.summary.copyCheck.nothingChecked', {
                defaultValue:
                  'Nicht prüfbar: Für keine Aufgabe liegen transkribierte Antworten von mindestens zwei Abgaben vor.',
              })}
            </span>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-4 text-sm text-slate-500 dark:text-slate-400">
            {result.checkedTaskNumbers
              ? t('Canvas.summary.copyCheck.noFindingsChecked', {
                  defaultValue: 'Keine Auffälligkeiten in den geprüften Aufgaben ({{tasks}}).',
                  tasks: result.checkedTaskNumbers.join(', '),
                })
              : t('Canvas.summary.copyCheck.noFindings', { defaultValue: 'Keine Auffälligkeiten gefunden.' })}
          </div>
        )
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-slate-700 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card">
          {flaggedPairs.map(pair => {
            const pairKey = `${pair.studentIdA}|${pair.studentIdB}`;
            const isExpanded = expandedPairKey === pairKey;
            return (
              <div key={pairKey} className="p-4">
                <button
                  type="button"
                  onClick={() => setExpandedPairKey(isExpanded ? null : pairKey)}
                  className="flex w-full items-center justify-between gap-3 text-left"
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                    )}
                    <span className="truncate text-sm font-semibold text-slate-800 dark:text-slate-200">
                      {displayName(pair.studentIdA)} ↔ {displayName(pair.studentIdB)}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">
                    {flagReasonLabel(pair)}
                    {' · '}
                    {t('Canvas.summary.copyCheck.taskCount', {
                      defaultValue: '{{count}} Teilaufgaben verglichen',
                      count: pair.taskCount,
                    })}
                    {pair.submissionSimilarity != null &&
                      ` · ${t('Canvas.summary.copyCheck.submissionSimilarity', {
                        defaultValue: 'gesamt {{value}} %',
                        value: Math.round(pair.submissionSimilarity * 100),
                      })}`}
                  </span>
                </button>

                {isExpanded && (
                  <div className="mt-3 space-y-2 pl-6">
                    {pair.perTask.length === 0 ? (
                      <p className="text-xs text-slate-400">
                        {t('Canvas.summary.copyCheck.noMatches', {
                          defaultValue: 'Keine einzelne Übereinstimmung ist für sich genommen auffällig.',
                        })}
                      </p>
                    ) : (
                      pair.perTask.map(detail => (
                        <div
                          key={detail.taskNumber}
                          className={cn(
                            'rounded-lg border p-3 text-xs',
                            detail.isWrongForBoth || detail.isVerbatim
                              ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/40'
                              : 'border-slate-100 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/40',
                            detail.rawSimilarity < 0.5 && 'opacity-60',
                          )}
                        >
                          <div className="mb-1.5 flex items-center justify-between gap-2 font-semibold text-slate-700 dark:text-slate-300">
                            <span>
                              {t('Canvas.summary.copyCheck.task', {
                                defaultValue: 'Aufgabe {{taskNumber}}',
                                taskNumber: detail.taskNumber,
                              })}
                              <span className="ml-2 font-normal text-slate-500 dark:text-slate-400">
                                {t('Canvas.summary.copyCheck.similarity', {
                                  defaultValue: '{{value}} % Übereinstimmung',
                                  value: Math.round(detail.rawSimilarity * 100),
                                })}
                              </span>
                            </span>
                            {detail.isVerbatim ? (
                              <span className="text-red-700 dark:text-red-300">
                                {t('Canvas.summary.copyCheck.verbatimMatch', { defaultValue: 'wörtlich übereinstimmend' })}
                              </span>
                            ) : (
                              detail.isWrongForBoth && (
                                <span className="text-red-700 dark:text-red-300">
                                  {t('Canvas.summary.copyCheck.sharedWrongAnswer', {
                                    defaultValue: 'gleiche falsche Antwort',
                                  })}
                                </span>
                              )
                            )}
                          </div>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <div className="min-w-0 break-words rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-2">
                              <p className="mb-0.5 font-semibold text-slate-500 dark:text-slate-400">
                                {displayName(pair.studentIdA)}
                              </p>
                              <p className="text-slate-700 dark:text-slate-300">{detail.studentAAnswer ?? '—'}</p>
                            </div>
                            <div className="min-w-0 break-words rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-2">
                              <p className="mb-0.5 font-semibold text-slate-500 dark:text-slate-400">
                                {displayName(pair.studentIdB)}
                              </p>
                              <p className="text-slate-700 dark:text-slate-300">{detail.studentBAnswer ?? '—'}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
